import { Path, FabricObject, Line, Triangle, util, Group, Rect, Circle } from 'fabric';
import { BaseDrawingHandler, BaseDrawingHandlerParams } from '../../base-class-handler/base-drawing-handler';
import { onOpenAnnotation } from '../../../services/annotation.service';

interface SeekablePlayer {
  currentTime: (seconds: number) => void;
}

export class VideoDrawingHandler extends BaseDrawingHandler {
  player: SeekablePlayer;
  private static readonly TEMP_VIDEO_DRAWING_ID = '__video_temp_drawing__';

  constructor(params: BaseDrawingHandlerParams, player: SeekablePlayer) {
    super(params);
    this.player = player;
    this.temporaryDrawingId = VideoDrawingHandler.TEMP_VIDEO_DRAWING_ID;
  }

  override clickInAnnotation(id: string): void {
    const annotation = this.findAnnotation(id);
    if (!annotation) return;

    this.player.currentTime(annotation.range.start);

    this.revertOpacity(id);
    this.disableAnnotationHover();

    onOpenAnnotation.emit({
        annotation,
        itemId: this.annotationPlugin.getItem().id,
        instanceId: this.instanceId,
      });
  }

  override clickOutAnnotation(id: string): void {
    const annotation = this.findAnnotation(id);
    if (!annotation) return;
    this.decreaseOpacity(id);
    this.enableAnnotationClick();

    const data = {
      messageId: annotation.id,
      itemId: this.annotationPlugin.getItem().id,
      instanceId: this.annotationPlugin.instanceId,
    };
    this.annotationPlugin.fire('annotationClickOut', data);
  }

  // override hoverOpen(annotationId: string): void {
  //   const annotation = this.findAnnotation(annotationId);
  //   if (!annotation) return;
  //   this.decreaseOpacity(annotation.id);

  //   const data = {
  //     annotation: annotation.data,
  //     itemId: this.annotationPlugin.getItem().id,
  //     instanceId: this.annotationPlugin.instanceId,
  //   };
  //   this.hoveredAnnotation = annotation;
  //   this.annotationPlugin.fire('annotationHoverIn', data);
  // }

  // override hoverClose(annotationId: string): void {
  //   const annotation = this.findAnnotation(annotationId);
  //   if (!annotation) return;
  //   this.revertOpacity(annotation.id);

  //   const data = {
  //     annotation: annotation.data,
  //     itemId: this.annotationPlugin.getItem().id,
  //     instanceId: this.annotationPlugin.instanceId,
  //   };
  //   this.hoveredAnnotation = null;
  //   this.annotationPlugin.fire('annotationHoverOut', data);
  // }

  override addAnnotationDrawings(
    id: string,
    shape: any,
    excludeFromExport = true,
    decreaseOpacityOnAdd = false,
    resolution?: any
  ): void {
    if (!shape) return;

    const { canvasFabric } = this;

    let annotationData: any;
    if (typeof shape === 'string') {
      try {
        annotationData = JSON.parse(shape);
      } catch (err) {
        return;
      }
    } else {
      annotationData = shape;
    }

    if (!annotationData?.objects) return;

    if (!excludeFromExport) {
      canvasFabric.remove(...canvasFabric.getObjects());
    }

    const fallbackResolution = { width: 1228, height: 695 };
    const sourceWidth = resolution?.width ?? fallbackResolution.width;
    const sourceHeight = resolution?.height ?? fallbackResolution.height;
    const scale = sourceWidth && sourceHeight ? canvasFabric.getWidth() / sourceWidth : 1;

    util.enlivenObjects(annotationData.objects).then((objects: any[]) => {
      objects.forEach((o: any) => {
        if (scale !== 1) {
          o.set('left', o.left * scale);
          o.set('top', o.top * scale);
          o.set('scaleX', o.scaleX * scale);
          o.set('scaleY', o.scaleY * scale);
          o.set('strokeWidth', o.strokeWidth / scale);
          o.setCoords();
        }
        o.set('id', id);
        o.set('selectable', false);
        o.set('hoverCursor', 'cursor');
        o.set('excludeFromExport', excludeFromExport);
        canvasFabric.add(o);
      });
      canvasFabric.renderAll();

      if (decreaseOpacityOnAdd) {
        this.decreaseOpacity(id);
      }
    });
  }

  override renderBaseShapesState(): void {}

  /**
   * Rehydrates a serialized Fabric state onto the canvas.
   * Ensures rehydrated objects are non-interactive and respects the
   * `disableSelectableObjects` flag to fully lock the scene when previewing.
   */
  override renderState(state: string, disableSelectableObjects = false): void {
    this.canvasFabric.getObjects().forEach((object) => {
      if (!object.excludeFromExport) {
        this.canvasFabric.remove(object);
      }
    });

    util.enlivenObjects(JSON.parse(state).objects).then((objs: any[]) => {
      objs.forEach((o) => {
        o.set('isStateObject', true);

        if (o.type === 'path' || o.type === 'group') {
          o.set({
            hasControls: false,
            lockScalingX: true,
            lockScalingY: true,
            lockScalingFlip: true,
            lockUniScaling: true,
          });
        }
        o.objectCaching = !this.selectedObject;

        this.canvasFabric.add(o);
      });
      this.canvasFabric.renderAll();

      this.canvasFabric.getObjects().forEach((obj) => {
        obj.set({ isStateObject: false });
        if (disableSelectableObjects) {
          obj.set({ selectable: false, hoverCursor: 'cursor' });
        }
      });
    });
  }

  override isInsideImage(pointer: { x: number; y: number }): boolean {
    const canvasFabric = this.canvasFabric;
    if (!canvasFabric.backgroundImage) {
      const maxWidth = canvasFabric.width;
      const maxHeight = canvasFabric.height;
      return !(pointer.x < 0 || pointer.x > maxWidth || pointer.y < 0 || pointer.y > maxHeight);
    }
    return super.isInsideImage(pointer);
  }

  /**
   * Binds drawing interactions for arrow, rectangle, circle and pencil tools.
   * Uses ALT to temporarily enable image dragging (disabling drawing mode),
   * clamps pointer movements to the canvas bounds, and fires 'object:modified'
   * to integrate with undo/redo history after creating or finishing shapes.
   */
  override startMouseEvents(): void {
    this.disableAnnotationHover();
    this.disableAnnotationClick();
    this.canvasEventGroups.clear('drawing');

    let shapeObject: FabricObject | undefined;
    let line: Line | undefined;
    let triangle: Triangle | undefined;
    let deltaX = 0;
    let deltaY = 0;
    let isDown = false;
    let origX = 0;
    let origY = 0;

    if (!this.firstTouch) {
      this.initUndoRedo(true);
      this.firstTouch = true;
    }

    ($(document) as any).on('keydown.' + this.instanceId, (o: KeyboardEvent) => {
      if (o.key === 'Alt' && !this.imageDragging) {
        this.canvasFabric.isDrawingMode = false;
        this.imageDragging = true;
      }
      if (
        (this.navigatorPlatform === 'mac' && (o.key === 'Backspace' || o.key === 'Delete')) ||
        (this.navigatorPlatform !== 'mac' && o.key === 'Delete')
      ) {
        this.deleteSelectedObject();
      }
    });

    ($(document) as any).on('keyup.' + this.instanceId, (o: KeyboardEvent) => {
      if (o.key === 'Alt' && this.imageDragging) {
        if (this.selectedObject === 'pencil') {
          this.canvasFabric.isDrawingMode = true;
        }
        this.imageDragging = false;
      }
    });

    this.canvasEventGroups.add('drawing', 'mouse:down', ((o: any) => {
      if (o.e.altKey === true) {
        return;
      }
      this.redoIndex = 0;
      this.redoStore = [];
      if (BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
        isDown = true;
        const pointer = this.clampToCanvas(this.canvasFabric.getPointer(o.e));
        origX = pointer.x;
        origY = pointer.y;

        if (this.selectedObject === 'arrow') {
          const points = [pointer.x, pointer.y, pointer.x, pointer.y] as [number, number, number, number];
          line = new Line(points, {
            strokeWidth: this.strokeWidth,
            fill: this.canvasFabric.drawColor,
            stroke: this.canvasFabric.drawColor,
            hoverCursor: 'default',
            originX: 'center',
            originY: 'center',
          });
          const centerX = (line.x1 + line.x2) / 2;
          const centerY = (line.y1 + line.y2) / 2;
          deltaX = (line.left ?? 0) - centerX;
          deltaY = (line.top ?? 0) - centerY;

          triangle = new Triangle({
            left: (line.get('x1') as number) + deltaX,
            top: (line.get('y1') as number) + deltaY,
            originX: 'center',
            originY: 'center',
            selectable: false,
            angle: -45,
            width: this.triangleSize,
            height: this.triangleSize,
            fill: this.canvasFabric.drawColor,
            hoverCursor: 'default',
          });
          this.canvasFabric.add(line, triangle);
        } else if (this.selectedObject === 'rectangle') {
          shapeObject = new Rect({
            left: origX,
            top: origY,
            originX: 'left',
            originY: 'top',
            width: pointer.x - origX,
            height: pointer.y - origY,
            angle: 0,
            fill: 'rgba(0,0,0,0)',
            stroke: this.canvasFabric.drawColor,
            selectable: false,
            strokeWidth: this.strokeWidth,
            hoverCursor: 'default',
          });
          this.canvasFabric.add(shapeObject);
        } else if (this.selectedObject === 'circle') {
          shapeObject = new Circle({
            left: origX,
            top: origY,
            originX: 'center',
            originY: 'center',
            width: Math.hypot(pointer.x - origX, pointer.y - origY),
            angle: 0,
            selectable: false,
            fill: 'rgba(0,0,0,0)',
            stroke: this.canvasFabric.drawColor,
            strokeWidth: this.strokeWidth,
            hoverCursor: 'default',
          });
          this.canvasFabric.add(shapeObject);
        }
      }
      if (this.selectedObject === 'pencil') {
        this.freeDrawTemp();
      }
    }));

    const { width: maxWidth, height: maxHeight } = this.getMaxDimensions();

    this.canvasEventGroups.add('drawing', 'mouse:move', ((o: any) => {
      if (o.e.altKey === true) {
        return;
      }
      if (BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
        if (!isDown) return;
        const pointer = this.clampToCanvas(this.canvasFabric.getPointer(o.e));
        if (this.selectedObject === 'arrow') {
          if (line && triangle) {
            line.set({
              x2: pointer.x,
              y2: pointer.y,
            });
            triangle.set({
              left: pointer.x + deltaX,
              top: pointer.y + deltaY,
              angle: this.calcArrowAngle(line.x1, line.y1, line.x2, line.y2),
            });
          }
        }
        if (this.selectedObject === 'circle' && shapeObject && 'set' in shapeObject) {
          let r = Math.hypot(origX - pointer.x, origY - pointer.y);
          const left = (shapeObject as any).left ?? 0;
          const top = (shapeObject as any).top ?? 0;
          r = Math.min(r, left, maxWidth - left);
          r = Math.min(r, top, maxHeight - top);
          (shapeObject as any).set({ radius: r });
        }
        if (this.selectedObject === 'rectangle' && shapeObject) {
          if (origX > pointer.x) {
            (shapeObject as any).set({ left: Math.abs(pointer.x) });
          }
          if (origY > pointer.y) {
            (shapeObject as any).set({ top: Math.abs(pointer.y) });
          }
          (shapeObject as any).set({ width: Math.abs(origX - pointer.x) });
          (shapeObject as any).set({ height: Math.abs(origY - pointer.y) });
        }
        this.canvasFabric.renderAll();
      }
    }));

    this.canvasEventGroups.add('drawing', 'mouse:up', ((o: any) => {
      if (o.e.altKey === true) {
        return;
      }
      if (BaseDrawingHandler.drawingTools.includes(this.selectedObject)) {
        isDown = false;
      }
      if (this.selectedObject === 'arrow' && line && triangle) {
        const group = new Group([line, triangle], {
          selectable: false,
          hoverCursor: 'default',
          lockScalingX: true,
          lockScalingY: true,
          hasControls: false,
        });
        this.canvasFabric.remove(line);
        this.canvasFabric.remove(triangle);
        this.canvasFabric.add(group);
        this.canvasFabric.fire('object:modified', { target: group });
        line = undefined;
        triangle = undefined;
      } else if (this.selectedObject === 'rectangle' && shapeObject) {
        this.canvasFabric.fire('object:modified', { target: shapeObject });
      } else if (this.selectedObject === 'circle' && shapeObject) {
        this.canvasFabric.fire('object:modified', { target: shapeObject });
      }
    }) );

    this.canvasEventGroups.add('drawing', 'path:created', ((opt: any) => {
      opt.path.selectable = false;
      shapeObject = opt.path as Path;
      opt.path.set({
        hoverCursor: 'cursor',
        lockScalingX: true,
        lockScalingY: true,
        lockScalingFlip: true,
        lockUniScaling: true,
        hasControls: false,
      });
      this.canvasFabric.fire('object:modified', { target: opt.path });
    }));
  }

  /**
   * Returns the interactive drawing bounds based on background image presence.
   * Falls back to canvas dimensions when no background image is present.
   */
  private getMaxDimensions(): { width: number; height: number } {
    const maxWidth = this.canvasFabric.backgroundImage
      ? this.canvasFabric.backgroundImage.width
      : this.canvasFabric.width;
    const maxHeight = this.canvasFabric.backgroundImage
      ? this.canvasFabric.backgroundImage.height
      : this.canvasFabric.height;
    return { width: maxWidth, height: maxHeight } as { width: number; height: number };
  }

  /**
   * Constrains a pointer position to stay within the canvas bounds to avoid
   * creating shapes outside the visible drawing area.
   */
  private clampToCanvas(pointer: { x: number; y: number }): { x: number; y: number } {
    const { width, height } = this.getMaxDimensions();
    return {
      x: Math.min(Math.max(0, pointer.x), width),
      y: Math.min(Math.max(0, pointer.y), height),
    };
  }

  /**
   * Computes the arrow head rotation angle in degrees given a start and end point.
   * Handles all quadrants and the degenerate horizontal/vertical cases.
   */
  private calcArrowAngle(x1: number, y1: number, x2: number, y2: number): number {
    let angle = 0;
    const x = x2 - x1;
    const y = y2 - y1;
    if (x === 0) {
      angle = y === 0 ? 0 : y > 0 ? Math.PI / 2 : (Math.PI * 3) / 2;
    } else if (y === 0) {
      angle = x > 0 ? 0 : Math.PI;
    } else {
      angle = x < 0 ? Math.atan(y / x) + Math.PI : y < 0 ? Math.atan(y / x) + 2 * Math.PI : Math.atan(y / x);
    }
    return (angle * 180) / Math.PI + 90;
  }
}
